"use client"

import { useEffect, useRef, useState } from "react"
import type mapboxgl from "mapbox-gl"
import "mapbox-gl/dist/mapbox-gl.css"

import { isOnlineLocation, resolveEventMapCoordinates } from "~/lib/location"

type EventLocationMapProps = {
  location?: string | null
  /**
   * #1400 — the event's stored latitude/longitude (captured from the
   * Mapbox picker at create/edit, #748). When either is a finite number,
   * the map centres and pins there directly — no geocode call is made, so
   * the pin can never land somewhere the address doesn't mean. When both
   * are omitted (e.g. the live address picker in the create/edit form,
   * where there is no saved event row yet), this falls back to geocoding
   * the free-text address, matching the previous behaviour for that case.
   * When they are explicitly `null` (a saved event with no coordinates on
   * record), no geocode fallback runs and no pin renders.
   */
  latitude?: number | null
  longitude?: number | null
}

const MAPBOX_STYLE = "mapbox://styles/mapbox/streets-v12"

export default function EventLocationMap({ location, latitude, longitude }: EventLocationMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const markerRef = useRef<mapboxgl.Marker | null>(null)
  const [shouldRender, setShouldRender] = useState(true)

  const hasCoordinateProps = latitude !== undefined || longitude !== undefined
  const storedCoordinates = resolveEventMapCoordinates(latitude, longitude)

  useEffect(() => {
    const resolvedLocation = location?.trim()
    if (!resolvedLocation || isOnlineLocation(resolvedLocation)) {
      setShouldRender(false)
      return
    }

    // #1400 — when the caller passed coordinate props at all, they are the
    // only source of truth: known-good coordinates pin directly, and a
    // known-absent (null) pair renders no pin. Only the no-props case (no
    // stored event row to read from) falls back to a fresh geocode.
    if (hasCoordinateProps && !storedCoordinates) {
      setShouldRender(false)
      return
    }

    setShouldRender(true)

    let isCancelled = false

    const initMap = async () => {
      if (!containerRef.current) return

      const token = process.env.NEXT_PUBLIC_MAPBOX_TOKEN
      if (!token) {
        console.warn("Mapbox token is not configured")
        return
      }

      const [{ default: mapboxgl }] = await Promise.all([import("mapbox-gl")])

      mapboxgl.accessToken = token

      try {
        let coordinates: [number, number]

        if (storedCoordinates) {
          coordinates = [storedCoordinates.lng, storedCoordinates.lat]
        } else {
          const geocodeResponse = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
              resolvedLocation
            )}.json?access_token=${token}&limit=1`
          )

          if (!geocodeResponse.ok) {
            console.warn("Failed to geocode location", geocodeResponse.statusText)
            return
          }

          const geocodeData = await geocodeResponse.json()
          const geocoded = geocodeData?.features?.[0]?.center

          if (
            !geocoded ||
            geocoded.length < 2 ||
            typeof geocoded[0] !== "number" ||
            typeof geocoded[1] !== "number"
          ) {
            console.warn("Unable to determine coordinates for location", resolvedLocation)
            return
          }

          coordinates = geocoded
        }

        if (isCancelled || !containerRef.current) return

        if (mapRef.current) {
          mapRef.current.remove()
          mapRef.current = null
        }

        const mapInstance = new mapboxgl.Map({
          container: containerRef.current,
          style: MAPBOX_STYLE,
          center: coordinates,
          zoom: 13,
        })

        const markerInstance = new mapboxgl.Marker({
          color: "#f97316",
        })
          .setLngLat(coordinates)
          .addTo(mapInstance)

        mapInstance.addControl(new mapboxgl.NavigationControl(), "top-right")

        mapRef.current = mapInstance
        markerRef.current = markerInstance
      } catch (error) {
        console.error("Failed to initialize Mapbox map:", error)
      }
    }

    void initMap()

    return () => {
      isCancelled = true
      if (markerRef.current) {
        markerRef.current.remove()
        markerRef.current = null
      }
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, [location, hasCoordinateProps, storedCoordinates?.lat, storedCoordinates?.lng])

  if (!shouldRender) return null

  return (
    <div
      ref={containerRef}
      className="h-full w-full overflow-hidden rounded-lg border border-border"
      role="presentation"
      aria-hidden="true"
    />
  )
}

